import { listViewStore, isItemPartOperation, MailRowDataPropertyGetter } from 'owa-mail-list-store';
import { getItemToShowFromNodeId } from 'owa-mail-store/lib/utils/conversationsUtils';
import type FlagStatus from 'owa-service/lib/contract/FlagStatus';
import type FlagType from 'owa-service/lib/contract/FlagType';
import { getUserConfiguration } from 'owa-session-store';
import { FlagCompleteEnum } from 'owa-mail-triage-action/lib/actions/helpers/getFlagCompleteState';
import type { OwaDate } from 'owa-datetime';
import {
    parse,
    now,
    isBefore,
    isSameDay,
    isSameWeek,
    addWeeks,
    differenceInCalendarDays,
} from 'owa-datetime';
import { assertNever } from 'owa-assert';

/**
 * Returns a map that determines whether to enable or disable MarkComplete and ClearFlag.
 * Enable logic is:
 *     - Mark Complete: Enabled if any items are marked 'Flagged' or 'NotFlagged'
 *     - Clear Flag: Enabled if any items are marked 'Flagged' or 'Complete'
 * @param enableMap map that will have its parameters be altered by this function
 */
export function getFlagUnflagEnableMap(): {
    shouldEnableMarkComplete: boolean;
    shouldEnableClearFlag: boolean;
} {
    const tableViewId = listViewStore.selectedTableViewId;
    const tableView = listViewStore.tableViews.get(tableViewId)!;
    const rowKeys = tableView.isInVirtualSelectAllMode
        ? tableView.rowKeys
        : [...tableView.selectedRowKeys.keys()];
    const selectedNodeIds = listViewStore.expandedConversationViewState.selectedNodeIds;

    let enableMap = {
        shouldEnableMarkComplete: false,
        shouldEnableClearFlag: false,
    };

    if (isItemPartOperation()) {
        for (const itemId of selectedNodeIds) {
            const item = getItemToShowFromNodeId(itemId);
            if (item) {
                updateEnableMap(item.Flag?.FlagStatus, enableMap);

                // Optimization: if all items in enableMap are enabled, then no need to continue loop.
                if (
                    enableMap.shouldEnableMarkComplete === true &&
                    enableMap.shouldEnableClearFlag === true
                ) {
                    break;
                }
            }
        }
    } else {
        for (const rowKey of rowKeys) {
            updateEnableMap(MailRowDataPropertyGetter.getFlagStatus(rowKey, tableView), enableMap);

            // Optimization: if all items in enableMap are enabled, then no need to continue loop.
            if (
                enableMap.shouldEnableMarkComplete === true &&
                enableMap.shouldEnableClearFlag === true
            ) {
                break;
            }
        }
    }

    return enableMap;
}

/**
 * Internal function to determine whether to enable or disable menu items.
 * @param flagStatus current item's flag status
 * @param enableMap object of booleans to update given flagStatus
 */
function updateEnableMap(
    flagStatus: FlagStatus | undefined | null,
    enableMap: { shouldEnableMarkComplete: boolean; shouldEnableClearFlag: boolean }
) {
    switch (flagStatus) {
        case 'Flagged':
            enableMap.shouldEnableMarkComplete = true;
            enableMap.shouldEnableClearFlag = true;
            break;
        case 'NotFlagged':
            enableMap.shouldEnableMarkComplete = true;
            break;
        case 'Complete':
            enableMap.shouldEnableClearFlag = true;
            break;
        case undefined:
        case null:
            // Case `undefined` or `null` is when an item has never been flagged or unflagged before. It acts as "NotFlagged".
            enableMap.shouldEnableMarkComplete = true;
            break;
        default:
            assertNever(flagStatus);
            break;
    }
}

/**
 * This function is used to help show selected state for the Flag/Unflag dropdown menu.
 * It determines this by calculating how far out the due date for the flag is relative to now,
 * and then returns a SelectedFlagState enum to determine which Flag/Unflag option should show selected state.
 */
export function getSelectedFlagState(): FlagCompleteEnum {
    const selectedFlagType: FlagType | undefined | null = getCurrentSelectionFlagType();

    // Check for unflagged item
    if (selectedFlagType == undefined || selectedFlagType.FlagStatus === 'NotFlagged') {
        return FlagCompleteEnum.clear;
    }

    // Check for completed item
    if (selectedFlagType.FlagStatus === 'Complete') {
        return FlagCompleteEnum.complete;
    }

    // Check for flagged (with no date selected) item
    if (selectedFlagType.StartDate == undefined || selectedFlagType.DueDate == undefined) {
        if (selectedFlagType.FlagStatus === 'Flagged') {
            return FlagCompleteEnum.noDate;
        } else {
            return FlagCompleteEnum.clear;
        }
    }

    // Determine how far out the dueDate is in terms of FlagCompleteEnum
    const userOptions = getUserConfiguration()?.UserOptions;
    const userWeekStartDay = userOptions?.WeekStartDay;
    const userTimeZone = userOptions?.TimeZone;
    if (userOptions == undefined || userWeekStartDay == undefined || userTimeZone == undefined) {
        return FlagCompleteEnum.clear;
    }

    const currDate: OwaDate = now();
    const dueDate: OwaDate = parse(userTimeZone, selectedFlagType.DueDate);

    if (isSameDay(currDate, dueDate) || isBefore(dueDate, currDate)) {
        // If the due date has passed, we still want to show "Today"
        return FlagCompleteEnum.today;
    } else if (differenceInCalendarDays(dueDate, currDate) === 1) {
        return FlagCompleteEnum.tomorrow;
    } else if (isSameWeek(currDate, dueDate, userWeekStartDay)) {
        return FlagCompleteEnum.thisWeek;
    } else if (isSameWeek(addWeeks(currDate, 1), dueDate, userWeekStartDay)) {
        return FlagCompleteEnum.nextWeek;
    } else {
        return FlagCompleteEnum.clear;
    }
}

/**
 * Internal function to get the current selected item's FlagType
 */
function getCurrentSelectionFlagType(): FlagType | undefined | null {
    const tableViewId = listViewStore.selectedTableViewId;
    const tableView = listViewStore.tableViews.get(tableViewId)!;
    const rowKeys = tableView.isInVirtualSelectAllMode
        ? tableView.rowKeys
        : [...tableView.selectedRowKeys.keys()];
    const selectedNodeIds = listViewStore.expandedConversationViewState.selectedNodeIds;

    // If the user has more than one item selected, return early. We don't show selected state here.
    if (rowKeys.length !== 1 || (isItemPartOperation() && selectedNodeIds.length !== 1)) {
        return undefined;
    }

    // Obtain current selected item's FlagType
    let flagType: FlagType | undefined | null;
    if (isItemPartOperation()) {
        // Expanded conversation view
        flagType = getItemToShowFromNodeId(selectedNodeIds[0])?.Flag;
    } else {
        flagType = MailRowDataPropertyGetter.getFlagType(rowKeys[0], tableView);
    }

    return flagType;
}
